import { money, percent } from "./format";
import { layoutFlow } from "./flow-layout";
import { useChartWidth } from "./use-chart-width";
import type { Flow, FlowKind, PlacedBlock } from "./flow-layout";
import styles from "./accounting.module.css";

/**
 * Where the money went — a flow chart (STORY-185, FL-D1–FL-D5): each location
 * into *Money in*, and *Money in* into staff, supplies and lab, occupancy, other
 * and profit. A loss shows a *Shortfall* block instead of profit (FL-D3).
 *
 * Hand-drawn SVG, like the other charts here. **It draws what `buildFlow` hands
 * it** and works nothing out. Every block and ribbon names its exact figure and
 * its share on hover, and the same sentence is printed under the chart, so no
 * amount is told by colour or height alone (FL-D4).
 */

/** Which class paints a block or ribbon of each kind: the existing tokens only. */
const FILL: Record<FlowKind, string | undefined> = {
  location: styles.flowIn,
  middle: styles.flowIn,
  shortfall: styles.flowShortfall,
  staff: styles.ohStaff,
  suppliesLab: styles.ohSupplies,
  occupancy: styles.ohOccupancy,
  other: styles.ohOther,
  profit: styles.flowProfit,
};

/** A long location name is cut so it cannot run under the chart; the title keeps it whole. */
function shorten(text: string, max = 26): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

export function FlowChart({
  flow,
  currency,
  label,
  testId,
}: {
  flow: Flow;
  currency: string;
  /** Names the chart for a screen reader. */
  label: string;
  testId?: string;
}): React.JSX.Element {
  // The real width of the card, so text keeps its size instead of being stretched up with the picture.
  const [box, width] = useChartWidth();

  if (flow.totalCents === 0) {
    return (
      <p
        className={styles.blank}
        data-testid={testId === undefined ? undefined : `${testId}-empty`}
      >
        No money came in or went out in this period.
      </p>
    );
  }

  const height = 250;
  // Room for a location's name on the left and a cost's on the right, at their natural size.
  const padLeft = Math.min(260, width * 0.25);
  const padRight = Math.min(210, width * 0.2);
  // Room above the blocks for the middle block's label, so it never sits on a ribbon.
  const padTop = 30;
  const pad = 8;
  const layout = layoutFlow(flow, {
    width: width - padLeft - padRight,
    height: height - padTop - pad,
    nodeWidth: 14,
    gap: 18,
  });
  const share = (cents: number): string => percent(cents / flow.totalCents);
  const say = (block: { label: string; cents: number }): string =>
    `${block.label} ${money(block.cents, currency)}, ${share(block.cents)} of the total`;

  const ribbonPath = (r: (typeof layout.ribbons)[number]): string => {
    const mid = (r.x0 + r.x1) / 2;
    return [
      `M ${String(r.x0)} ${String(r.y0)}`,
      `C ${String(mid)} ${String(r.y0)}, ${String(mid)} ${String(r.y2)}, ${String(r.x1)} ${String(r.y2)}`,
      `L ${String(r.x1)} ${String(r.y3)}`,
      `C ${String(mid)} ${String(r.y3)}, ${String(mid)} ${String(r.y1)}, ${String(r.x0)} ${String(r.y1)}`,
      "Z",
    ].join(" ");
  };

  const block = (b: PlacedBlock, side: "left" | "middle" | "right"): React.JSX.Element => {
    const mid = b.y + b.height / 2;
    const text = `${money(b.cents, currency)} · ${share(b.cents)}`;
    return (
      <g key={b.id}>
        <title>{say(b)}</title>
        <rect className={FILL[b.kind]} x={b.x} y={b.y} width={b.width} height={b.height} rx={2} />
        {side === "left" && (
          <text className={styles.flowText} x={b.x - 8} y={mid + 4} textAnchor="end" fontSize={12}>
            <tspan>{shorten(b.label)} </tspan>
            <tspan className={styles.flowFigure}>{text}</tspan>
          </text>
        )}
        {side === "right" && (
          <text className={styles.flowText} x={b.x + b.width + 8} y={mid + 4} fontSize={12}>
            <tspan>{b.label} </tspan>
            <tspan className={styles.flowFigure}>{text}</tspan>
          </text>
        )}
        {side === "middle" && (
          <text
            className={styles.flowFigure}
            x={b.x + b.width / 2}
            y={b.y - 8}
            textAnchor="middle"
            fontSize={12}
          >
            {b.label} {money(b.cents, currency)}
          </text>
        )}
      </g>
    );
  };

  return (
    <figure className={styles.flowFigureBox} data-testid={testId}>
      <div ref={box}>
        <svg
          className={styles.chart}
          viewBox={`0 0 ${String(width)} ${String(height)}`}
          role="img"
          aria-label={label}
        >
          <g transform={`translate(${String(padLeft)} ${String(padTop)})`}>
            {layout.ribbons.map((r) => (
              <path
                key={r.id}
                className={`${FILL[r.kind] ?? ""} ${styles.flowRibbon}`}
                d={ribbonPath(r)}
              >
                <title>
                  {r.from === "middle" ? "Money in to " : "Into money in from "}
                  {say({
                    label:
                      [...layout.sources, ...layout.sinks].find(
                        (b) => b.id === (r.from === "middle" ? r.to : r.from),
                      )?.label ?? "",
                    cents: r.cents,
                  })}
                </title>
              </path>
            ))}
            {layout.sources.map((b) => block(b, "left"))}
            {block(layout.middle, "middle")}
            {layout.sinks.map((b) => block(b, "right"))}
          </g>
        </svg>
      </div>
      <figcaption
        className={styles.note}
        data-testid={testId === undefined ? undefined : `${testId}-caption`}
      >
        Came from: {flow.sources.map((b) => say(b)).join("; ")}. Went to:{" "}
        {flow.sinks.map((b) => say(b)).join("; ")}.
      </figcaption>
    </figure>
  );
}
